import React, { useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { register, loginEmail, checkEmail } from '../services/api';

export default function AuthPage({ onAuth, onPreview, googleError, googleLoading: googleLoadingProp }) {
  const ink = '#14214d';
  const { signIn } = useSignIn() || {};
  const [googleLoading, setGoogleLoading] = useState(false);
  const [step, setStep] = useState('email'); // email, name, familyCode, password
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [familyCode, setFamilyCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await checkEmail(email);
      const exists = response.exists;
      setIsNewUser(!exists);
      setStep(exists ? 'password' : 'name');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    setError('');
    setStep('familyCode');
  };

  const handleFamilyCodeSubmit = (e) => {
    e.preventDefault();
    // Family code is optional, just proceed to password
    setError('');
    setStep('password');
  };

  const handleGoogleAuth = async () => {
    if (!signIn) return;
    setGoogleLoading(true);
    setError('');
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: window.location.origin + '/sso-callback',
        redirectUrlComplete: window.location.origin + '/',
      });
    } catch (err) {
      console.error('Google auth error:', err);
      setError('Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password');
      return;
    }
    if (isNewUser && password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      let user;
      if (isNewUser) {
        user = await register(email, password, name, familyCode || undefined);
      } else {
        user = await loginEmail(email, password);
      }
      onAuth(user);
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        background: 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(22px)',
        WebkitBackdropFilter: 'blur(22px)',
        border: '1px solid rgba(255,255,255,0.42)',
        borderRadius: 28,
        boxShadow: '0 16px 38px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.46)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 12, position: 'relative', zIndex: 1 }}>
          <h1 style={{ 
            fontFamily: "'Dreaming Outloud Pro', 'Syne', sans-serif",
            fontSize: 32, 
            fontWeight: 700, 
            color: ink,
            marginBottom: 8,
            textShadow: '0 2px 12px rgba(255,255,255,0.36)'
          }}>
            Oi My AI
          </h1>
          <p style={{ color: 'rgba(20,33,77,0.8)', fontSize: 15 }}>
            Your personal AI companion
          </p>
        </div>

        <button
          type="button"
          onClick={handleGoogleAuth}
          disabled={googleLoading || googleLoadingProp}
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: 14,
            borderRadius: 16,
            background: 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(255,255,255,0.6)',
            color: ink,
            fontSize: 15,
            fontWeight: 600,
            cursor: googleLoading ? 'default' : 'pointer',
            opacity: googleLoading ? 0.7 : 1,
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 0 1 9.5 24c0-1.59.28-3.14.76-4.59l-7.98-6.19A23.99 23.99 0 0 0 0 24c0 3.77.9 7.35 2.56 10.52l7.97-5.93z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 5.93C6.51 42.62 14.62 48 24 48z"/></svg>
          {(googleLoading || googleLoadingProp) ? 'Signing in...' : 'Continue with Google'}
        </button>

        {/* Google/Clerk sync error — shown when OAuth completes but backend sync fails */}
        {googleError && (
          <div style={{
            background: 'rgba(255,80,80,0.15)',
            border: '1px solid rgba(255,80,80,0.35)',
            borderRadius: 12,
            padding: '10px 14px',
            color: '#ff4444',
            fontSize: 13,
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}>
            {googleError}
          </div>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{ flex: 1, height: 1, background: 'rgba(20,33,77,0.2)' }} />
          <span style={{ color: 'rgba(20,33,77,0.5)', fontSize: 12 }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'rgba(20,33,77,0.2)' }} />
        </div>

        <form onSubmit={step === 'email' ? handleEmailSubmit : (step === 'name' ? handleNameSubmit : (step === 'familyCode' ? handleFamilyCodeSubmit : handleFinalSubmit))} style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          
          {step === 'email' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: 'rgba(20,33,77,0.85)', fontSize: 13, marginLeft: 4 }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="name@example.com"
                required
                style={inputStyle}
              />
            </div>
          )}

          {step === 'name' && (
             <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: 'rgba(20,33,77,0.85)', fontSize: 13, marginLeft: 4 }}>What should we call you?</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your Name"
                required={false}
                style={inputStyle}
              />
            </div>
          )}

          {step === 'familyCode' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: 'rgba(20,33,77,0.85)', fontSize: 13, marginLeft: 4 }}>
                Have a family code? (Optional)
              </label>
              <input
                type="text"
                value={familyCode}
                onChange={e => setFamilyCode(e.target.value.toUpperCase())}
                placeholder="e.g., SMITH-A3K9"
                style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: 1 }}
              />
              <p style={{ color: 'rgba(20,33,77,0.5)', fontSize: 11, marginLeft: 4, marginTop: 4 }}>
                If someone shared a family code with you, enter it here to join their AI assistant.
              </p>
            </div>
          )}

          {(step === 'password') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: 'rgba(20,33,77,0.85)', fontSize: 13, marginLeft: 4 }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={inputStyle}
              />
            </div>
          )}

          {error && (
            <div style={{ color: '#ff6b6b', fontSize: 13, textAlign: 'center', background: 'rgba(255,0,0,0.1)', padding: 8, borderRadius: 8 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              marginTop: 8,
              padding: 16,
              borderRadius: 16,
              background: 'linear-gradient(135deg, #fff 0%, #ddd 100%)',
              color: ink,
              border: 'none',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              opacity: isLoading ? 0.7 : 1,
              transform: isLoading ? 'scale(0.98)' : 'scale(1)',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(255,255,255,0.15)'
            }}
          >
            {isLoading ? 'Please wait...' : (step === 'email' ? 'Continue' : (step === 'name' ? 'Continue' : (step === 'familyCode' ? (familyCode ? 'Join Family' : 'Skip') : (isNewUser ? 'Create Account' : 'Sign In'))))}
          </button>
        </form>
        <button
          type="button"
          onClick={onPreview}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.78)',
            textShadow: '0 1px 0 rgba(255,255,255,0.28)',
            fontSize: 12,
            textDecoration: 'underline',
            cursor: 'pointer',
            marginTop: -4,
          }}
        >
          Continue in preview mode
        </button>
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '16px',
  borderRadius: 16,
  background: 'rgba(255,255,255,0.32)',
  border: '1px solid rgba(255,255,255,0.42)',
  color: '#14214d',
  fontSize: 16,
  outline: 'none',
  backdropFilter: 'blur(12px)',
};
